import { useEffect, useRef, useState, useCallback } from 'react';
import { HumanHandoverWebSocketService } from '@/services/websocket';
import { useUserStore } from '@/stores/user/userStore';
import { useHumanHandoverStore } from '@/stores/humanHandover/humanHandoverStore';

/**
 * Hook for managing human handover WebSocket connections
 *
 * @param {Object} config - Configuration for the WebSocket
 * @param {string} config.conversationId - ID of the conversation
 * @param {string} config.token - Authentication token
 * @param {boolean} config.isInApp - Whether this is in the app (vs. shared/embedded)
 * @param {boolean} config.isInHumanHandoverApp - Whether this is in the human handover app
 * @param {Function} config.onMessageReceived - Callback for when a message is received
 * @param {Function} config.onAgentJoined - Callback for when an agent joins
 * @param {Function} config.onAgentResolved - Callback for when an agent resolves the conversation
 * @param {Object} [config.wsRef] - Optional ref to store the WebSocket instance
 * @returns {Object} WebSocket state and methods
 */
const useHumanHandoverWebSocket = (config) => {
  const internalWsRef = useRef(null);
  const wsRef = config.wsRef || internalWsRef;
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const currentConversationId = useRef(null);


  const userData = useUserStore((state) => state.user);
  const liveAgentData = useHumanHandoverStore((state) => state.userOrganization);
  const setLiveAgentData = useHumanHandoverStore((state) => state.setUserOrganization);

  // Handle typing events
  const onTypingStart = useCallback((parsedData) => {
    setIsTyping(true);
    const user = parsedData.data?.user_name || parsedData.data?.agent_name || 'Someone';
    setTypingUser(user);
  }, []);

  const onTypingStop = useCallback((parsedData) => {
    setIsTyping(false);
    setTypingUser(null);
  }, []);

  // Track connection attempts to prevent duplicates
  const connectionAttemptRef = useRef(null);

  // Track if agent has already joined to prevent duplicate "joined" messages
  const hasAgentJoinedRef = useRef(false);
  
  // Initialize WebSocket connection
  const connect = useCallback((conversationId) => {
    if (!conversationId) {
      console.error('Cannot connect: Missing conversation ID');
      setError(new Error('Missing conversation ID'));
      return;
    }

    // Don't connect if already connecting or connected to the same conversation
    if (isConnecting && connectionAttemptRef.current === conversationId) {
      return;
    }
    
    if (wsRef.current && wsRef.current.getState().isConnected && currentConversationId.current === conversationId) {
      return;
    }

    setIsConnecting(true);
    setError(null);
    
    // Track this connection attempt
    connectionAttemptRef.current = conversationId;

    // Store the conversation ID for later use
    currentConversationId.current = conversationId;

    // Reset agent joined flag for new conversation
    hasAgentJoinedRef.current = false;

    try {
      // Close any existing connection
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }

      // Create WebSocket URL
      const wsUrl = `${import.meta.env.VITE_APP_BASE_WEBSOCKET}/human-handover/conversations/${conversationId}/chat`;

      // Create WebSocket service
      wsRef.current = new HumanHandoverWebSocketService(
        {
          url: wsUrl,
          authToken: config.token,
          isShared: !config.isInApp,
          isInHumanHandoverApp: config.isInHumanHandoverApp
        },
        {
          onOpen: () => {
            setIsConnected(true);
            setIsConnecting(false);
            connectionAttemptRef.current = null;
          },
          onClose: (event) => {
            setIsConnected(false);
            setIsConnecting(false);
            
            // Only clear the conversation ID if it matches the one being closed
            if (currentConversationId.current === conversationId) {
              currentConversationId.current = null;
            }
            
            // Clear connection attempt reference if it matches
            if (connectionAttemptRef.current === conversationId) {
              connectionAttemptRef.current = null;
            }
          },
          onError: (event) => {
            setError(event.error || new Error('WebSocket error'));
            setIsConnecting(false);
            
            // Clear connection attempt reference on error
            if (connectionAttemptRef.current === conversationId) {
              connectionAttemptRef.current = null;
            }
          },
          onMessageReceived,
          onAgentJoined,
          onAgentResolved,
          onTypingStart,
          onTypingStop
        }
      );

      // Connect to the WebSocket server
      wsRef.current.connect().catch((error) => {
        console.error('Error connecting to WebSocket:', error);
        setError(error);
        setIsConnecting(false);
        
        // Clear connection attempt reference on error
        if (connectionAttemptRef.current === conversationId) {
          connectionAttemptRef.current = null;
        }
      });
    } catch (error) {
      console.error('Error initializing WebSocket:', error);
      setError(error);
      setIsConnecting(false);
      
      // Clear connection attempt reference on error
      if (connectionAttemptRef.current === conversationId) {
        connectionAttemptRef.current = null;
      }
    }
  }, [config]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    const conversationId = currentConversationId.current;
    
    // Clear any pending connection attempts
    if (connectionAttemptRef.current) {
      connectionAttemptRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.disconnect();
      wsRef.current = null;
      setIsConnected(false);
      setIsConnecting(false);
    }
    
    // Reset conversation ID
    currentConversationId.current = null;
    hasAgentJoinedRef.current = false;

    return conversationId; // Return the conversation ID that was disconnected
  }, []);

  // Send a message
  const sendMessage = useCallback((text, isQuickResponse = false, images = []) => {
    if (!wsRef.current) {
      console.error('Cannot send message: WebSocket not initialized');
      return false;
    }

    const additionalData = {};

    // Add agent info if in human handover app
    if (config.isInHumanHandoverApp && liveAgentData && config.isInApp) {
      additionalData.agent_name = liveAgentData.name;
      additionalData.agent_profile_pic = liveAgentData.image;
      additionalData.agent_role = liveAgentData.agent_title;
      additionalData.agent_title = liveAgentData.agent_title;
    }

    return wsRef.current.sendUserMessage(text, isQuickResponse, images, additionalData);
  }, [config.isInHumanHandoverApp, userData, liveAgentData]);

  // Send a control message (human_handover_taken or human_handover_resolved)
  const sendControlMessage = useCallback((type, conversationId, additionalData = {}) => {
    if (!wsRef.current) {
      console.error('Cannot send control message: WebSocket not initialized');
      return false;
    }

    return wsRef.current.sendControlMessage(type, conversationId, additionalData);
  }, []);

  // Send typing start event
  const sendTypingStart = useCallback(() => {
    if (!wsRef.current) {
      console.error('Cannot send typing start: WebSocket not initialized');
      return false;
    }

    return wsRef.current.sendTypingStart();
  }, []);

  // Send typing stop event
  const sendTypingStop = useCallback(() => {
    if (!wsRef.current) {
      console.error('Cannot send typing stop: WebSocket not initialized');
      return false;
    }

    return wsRef.current.sendTypingStop();
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Clear any pending connection attempts
      if (connectionAttemptRef.current) {
        connectionAttemptRef.current = null;
      }
      
      if (wsRef.current) {
        // Use a more forceful disconnect for cleanup
        try {
          wsRef.current.disconnect(1000, 'Component unmounting');
        } catch (e) {
          console.error('Error during cleanup disconnect:', e);
        }
        wsRef.current = null;
      }
      
      // Reset state
      setIsConnected(false);
      setIsConnecting(false);
      currentConversationId.current = null;
      hasAgentJoinedRef.current = false;
    };
  }, []);

  const onMessageReceived = useCallback((data) => {
    if (config.onMessageReceived) {
      // Update store with actual agent data from WebSocket
      if (data?.data?.agent_name) {
        const agentData = {
          name: data.data.agent_name,
          agent_name: data.data.agent_name,
          image: data.data.agent_profile_pic,
          agent_profile_pic: data.data.agent_profile_pic,
          agent_role: data.data.agent_role,
          agent_title: data.data.agent_title
        };
        setLiveAgentData(agentData);

        // Only trigger onAgentJoined callback once when agent info is first received
        // This ensures the header gets updated with the correct agent info
        // but prevents the "Team member has joined the chat" message from appearing multiple times
        if (config.onAgentJoined && !hasAgentJoinedRef.current) {
          hasAgentJoinedRef.current = true;
          const infoAgent = {
            name: data.data.agent_name,
            agent_name: data.data.agent_name,
            image: data.data.agent_profile_pic,
            agent_profile_pic: data.data.agent_profile_pic,
            agent_title: data.data.agent_title,
            agent_role: data.data.agent_role,
          };
          config.onAgentJoined({ data, infoAgent });
        }
      }
      config.onMessageReceived(data);
    }
  }, [config, setLiveAgentData]);

  const onAgentJoined = useCallback((data) => {
    if (config.onAgentJoined) {
      // human_handover_taken message has data: null, so agent info will be undefined
      // This is OK - we'll update it when the first message arrives
      const infoAgent = {
        name: data?.data?.agent_name,
        agent_name: data?.data?.agent_name,
        image: data?.data?.agent_profile_pic,
        agent_profile_pic: data?.data?.agent_profile_pic,
        agent_title: data?.data?.agent_title,
        agent_role: data?.data?.agent_role,
      };
      config.onAgentJoined({ data, infoAgent });
    }
  }, [config]);

  const onAgentResolved = useCallback((data) => {
    if (config.onAgentResolved) {
      const infoAgent = {
        name: liveAgentData?.name,
        agent_name: liveAgentData?.name,
        image: liveAgentData?.image,
        agent_profile_pic: liveAgentData?.image,
        agent_title: liveAgentData?.agent_title,
        agent_role: liveAgentData?.agent_title,
      };
      config.onAgentResolved({ data, infoAgent });
    }
  }, [config, liveAgentData]);

  return {
    connect,
    disconnect,
    sendMessage,
    sendControlMessage,
    sendTypingStart,
    sendTypingStop,
    isConnected,
    isConnecting,
    error,
    isTyping,
    typingUser
  };
};

export default useHumanHandoverWebSocket;
